import { db, database, storage, functions } from './firebase/firebase'
import PictureHelper from './utilities/PictureHelper';
import { IMAGE_TYPES, MAX_IMAGE_SIZE } from './utilities/PictureHelper';
import joi from 'joi-browser';
import { Business, Certification, certification_status, Conversation, InventoryItem, Message, Notification, Order, OrderBundle, User, UserSession } from './model'
import { businessSchema, inventoryItemSchema, orderSchema, orderBundleSchema, preferencesSchema, userSchema } from './schemas';
import ClientRelationship from './model/ClientRelationship';
import Invitation from './model/Invitation';
import { GeoFirestore } from 'geofirestore';
import firebase from 'firebase';
import axios from 'axios';
import Product from './model/Product';
import Picture from './model/Picture';
import ProductListing from './model/ProductListing';
import PackFormat from './model/PackFormat';
import { UserService, BusinessService, OrganizationService, OrderBundleService } from './api';
import { unmarshal } from "core/utilities/ModelUtils";
import moment from "moment";
import {profileStore} from "../stores";

/** Unsubscribers */
var unsubscribers = {};
var notificationUnsubscriber = null;
var userUnsubscriber = null;
var businessUnsubscriber = null;
var conversationsUnsubscriber = null;

const GMAPS_API_KEY = "AIzaSyBtA2jCaVYVSxrG6apNSV9zz4GCrmUW1fc";

// Constants
const MAX_FILE_SIZE = 15; //MB
const DEFAULT_MESSAGE_RETRIEVAL_COUNT = 25;
const SERVER_TIME = firebase.firestore.FieldValue.serverTimestamp();

// Firestore References
const certificationDB = db.collection("certifications");
const certificationStorage = storage.ref('certifications');
const conversationDB = db.collection("conversations");
const attachmentDB = db.collection("attachments");
const attachmentStorage = storage.ref('attachments');
const businessDB = db.collection("businesses");
const sessionDB = db.collection("sessions");
const productDB = db.collection("products");
const productListingDB = db.collection("product_listings");
const pictureDB = db.collection("pictures");
const orderDB = db.collection("orders");
const orderBundleDB = db.collection("order_bundles");
const userDB = db.collection("users");

// Storage References
const pictureStorage = storage.ref('pictures');

// APIS
var oneSignalAPI = axios.create({
    baseURL: 'https://onesignal.com/api/v1'
});

class DatabaseClient {
     /***** USER *****/
    setUserListener(uid, callbackFn = () => {}) {
        this.removeUserListener();
        userUnsubscriber = db.collection('users').doc(uid).onSnapshot( async (snapshot) => {
            if (snapshot.exists) {
                const user = await UserService.get(snapshot.id);
                console.log("User service got ", user);
                callbackFn(user);
            }
        }, (e) => {
            console.error("User listener onSnapshot failed")
            console.error(e);
        });
    }

    removeUserListener() {
        if (userUnsubscriber) {
            userUnsubscriber();
        }
        userUnsubscriber = null;
    }

     /***** BUSINESS *****/
    async createProfilePicture(picture) {
        // First validate and compress the picture
        if (!PictureHelper.validateImageType(picture)) {
            let errmsg = 'The file extension is must be one of the following: ';
            for (let i = 0; i < IMAGE_TYPES.length - 1; i++) {
                errmsg += IMAGE_TYPES[i] + ", "
            }
            errmsg += IMAGE_TYPES[IMAGE_TYPES.length - 1] + '.'
            throw new Error(errmsg);
        }
        let metadata = {
            contentType: picture.type,
        };
        let compressedPic = await PictureHelper.compressImage(picture);
        if (!PictureHelper.validateImageSize(compressedPic)) {
            throw new Error('Could not compress image below ' + MAX_IMAGE_SIZE + '. Please try a different image.');
        }
        let pictureRef = db.collection('profile_pictures').doc();
        let pictureID = pictureRef.id;
        let pictureStorageRef = storage.ref('profile_pictures').child(pictureID);
        await pictureStorageRef.put(compressedPic, metadata);
        let url = await pictureStorageRef.getDownloadURL();
        await pictureRef.set({url: url});
        return pictureID;
    }

    setBusinessListener(uid, callbackFn = () => {}) {
        this.removeBusinessListener();
        businessUnsubscriber = db.collection('businesses').doc(uid).onSnapshot( async (snapshot) => {
            if (snapshot.exists) {
                // Normally we could just grab snapshot.data(), but we also need to grab certs
                // This will act as a quick fix
                // TODO: refactor code to make this more efficient - make a helper method common to this and getBusiness()
                const business = await BusinessService.get(uid);
                callbackFn(business);
            }
        }, (e) => {
            console.error("business listener onSnapshot failed")
            console.error(e);
        });
    }

    removeBusinessListener() {
        if (businessUnsubscriber) {
            businessUnsubscriber();
        }
        businessUnsubscriber = null;
    }

    /***** DEALS *****/
    /**
     * Inserts a deal entity into the database, with the option of adding an array of pictures
     * corresponding to the deal.  Function may be called without the pictures parameter, in
     * which case any pictures must be added separately using {@link #createDealPictures}.
     * @param {InventoryItem} deal InventoryItem representing the deal
     * @param {Array<File>} pictures Optional.  Array of picture files.
     * @returns the auto-generated ID for the deal
     */
    async createDeal(deal, pictures) {
        // Create a regular JS Object from the deal
        let _deal = deal.formatForDB();

        // Validate the deal
        const result = joi.validate(_deal, inventoryItemSchema);
        if (result.error) {
            console.error(result.error);
            throw new Error(result.error.details[0].message);
        }

        let pictureIDs = [];
        if (pictures) {
            for (let i = 0; i < pictures.length; i++) {
                let pictureID = await this.createDealPicture(pictures[i]);
                pictureIDs.push(pictureID);
            }
        }

        if (pictureIDs.length > 0) {
            _deal.pictures = pictureIDs;
        }
        
        let newDeal = {
            uid: _deal['uid'],
            archived: false,
            currentVersion: 0,
            timestamp: +(new Date())
        };
        let ref = await db.collection('deals').add(newDeal);
        let id = ref.id;
        await ref.collection('versions').doc('0').set(_deal);
        return id;
    }

    async editDeal(dealID, deal, pictures) {
        // Create a regular JS Object from the deal
        let _deal = deal.formatForDB();
        // Validate the deal
        const result = joi.validate(_deal, inventoryItemSchema);
        if (result.error) {
            console.error(result.error);
            throw new Error(result.error.details[0].message);
        }

        let ref = db.collection('deals').doc(dealID);
        let snapshot = await ref.get();
        if (!snapshot.exists) {
            return;
        }
        let dealInfo = snapshot.data();
        if (pictures) {
            let pictureIDs = [];
            for (let i = 0; i < pictures.length; i++) {
                let pictureID = await this.createDealPicture(pictures[i]);
                pictureIDs.push(pictureID);
            }
            _deal['pictures'] = pictureIDs;
        }
        else {
            let snapshot = await db.collection('deals').doc(dealID).collection('versions').doc(dealInfo['currentVersion'] + '').get();
            if (!snapshot.exists) {
                _deal['pictures'] = [];
            }
            else {
                _deal['pictures'] = snapshot.data()['pictures'] || [];
            }
        }
        dealInfo['currentVersion']++;
        await ref.collection('versions').doc(dealInfo['currentVersion'] + '').set(_deal);
        await ref.update(dealInfo);
    }

    async createDealPicture(picture) {
        // First validate and compress the picture
        if (!PictureHelper.validateImageType(picture)) {
            let errmsg = 'The file extension is must be one of the following: ';
            for (let i = 0; i < IMAGE_TYPES.length - 1; i++) {
                errmsg += IMAGE_TYPES[i] + ", "
            }
            errmsg += IMAGE_TYPES[IMAGE_TYPES.length - 1] + '.'
            throw new Error(errmsg);
        }
        let metadata = {
            contentType: picture.type,
        };
        let compressedPic = await PictureHelper.compressImage(picture);
        if (!PictureHelper.validateImageSize(compressedPic)) {
            throw new Error('Could not compress image below ' + MAX_IMAGE_SIZE + '. Please try a different image.');
        }
        let pictureRef = db.collection('deal_pictures').doc();
        let pictureID = pictureRef.id;
        let pictureStorageRef = storage.ref('deal_pictures').child(pictureID);
        await pictureStorageRef.put(compressedPic, metadata);
        let url = await pictureStorageRef.getDownloadURL();
        await pictureRef.set({url: url});
        return pictureID;
    }

    /**
     * Retrieves the deal corresponding to the dealID
     * If a version is specified, that version is retrieved. Otherwise, the latest version
     * is retrieved.
     * @param {string} dealID The unique ID of the deal 
     * @param {number} version (Optional) The version of the deal to retrieve
     * @return the specified deal/version
     */
    async getDeal(dealID, version, allowArchived = false) {
        let dealInfoRef = await db.collection('deals').doc(dealID);
        let dealInfoSnapshot = await dealInfoRef.get();
        if (!dealInfoSnapshot.exists) {
            return null;
        }
        let dealInfo = dealInfoSnapshot.data();

        if (!allowArchived && dealInfo['archived'] === true) {
            return null;
        }
        let versionToRetrieve = (version !== undefined) ? version : dealInfo['currentVersion'];
        let dealSnapshot = await dealInfoRef.collection('versions').doc(versionToRetrieve + '').get();
        if (!dealSnapshot.exists) {
            return null;
        }
        let data = dealSnapshot.data();
        let seller = await BusinessService.get(dealInfo['uid']);
        let deal = new InventoryItem(data.delivery, data.foodOption, dealID, data.isLocallyGrown, data.isOrganic,
                                    data.name, data.notes, data.pickup, data.pictures, data.price,
                                    data.priceUnit, data.public, data.quantity, data.quantityUnit,
                                    data.reasonForPost, seller, dealInfo['uid'], data.useByDate, versionToRetrieve);

        // TODO: allow multiple pictures
        if (deal.pictures.length > 0) {
            // Only gets the first picture in deal right now
            let pictureID = deal.pictures[0];
            let url = await this.getDealPictureURL(pictureID);
            deal['picture'] = url;
            // delete deal['pictures'];
        }
        return deal;
    }

    async getDealPictureURL(pictureID) {
        let pictureSnapshot = await db.collection('deal_pictures').doc(pictureID).get();
        if (!pictureSnapshot.exists) {
            return null;
        }
        return pictureSnapshot.data()['url'];
    }

    async archiveDeal(dealID) {
        await db.collection('deals').doc(dealID).update({archived: true});
    }

    /**
     * Gets the deals listed by a seller as specified by their UID
     * @param {string} uid 
     * @return an array of deals
     */
    async getDealsBySeller(uid) {
        let query = db.collection('deals').where("uid", "==", uid).where("archived", "==", false);
        let snapshot = await query.get();
        if (snapshot.empty) {
            return [];
        }
        let dealInfos = snapshot.docs;
        let deals = await Promise.all(dealInfos.map( async (dealInfoSnapshot) => {
            let deal = this.getDeal(dealInfoSnapshot.id);
            return deal;
        }));
        return deals;
    }

    async getProductsBySeller(uid) {
        let query = productDB.where("uid", "==", uid);
        let snapshot = await query.get();
        if (snapshot.empty) {
            return [];
        }
        let dealInfos = snapshot.docs;
        let deals = await Promise.all(dealInfos.map( async (dealInfoSnapshot) => {
            let deal = this.getProduct(dealInfoSnapshot.id);
            return deal;
        }));
        return deals.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }
    
    /**
     * Retrieves all deals that should be shown to a given buyer
     * @param {*} uid 
     * @return an object that holds each deal as indexed by their dealID
     */
    async getPrivateDealsForBuyer(uid) {
        let sellerUIDs = await this.getSellerUIDsForBuyer(uid);
        let collectiveSellerDeals = await Promise.all(sellerUIDs.map( async (sellerUID) => {
            let sellerDeals = this.getDealsBySeller(sellerUID);
            return sellerDeals;
        }));
        var merged = [].concat.apply([], collectiveSellerDeals);
        return merged;
    }

    /**
     * Retrieves all deals from unaffiliated sellers that should be shown to a given buyer
     * @param {*} uid 
     * @return an object that holds each deal as indexed by their dealID
     */
    async getPublicDealsForBuyer(uid,sellerUIDs) {
        let collectiveSellerDeals = await Promise.all(sellerUIDs.map( async (sellerUID) => {
            let sellerDeals = this.getDealsBySeller(sellerUID);
            return sellerDeals;
        }));
        var merged = [].concat.apply([], collectiveSellerDeals);
        return merged;
    }

    setDealListener(dealID, callbackFnEdited, callbackFnArchived) {
        this.removeDealListener(dealID);
        let dealInfoRef = db.collection('deals').doc(dealID);
        unsubscribers[dealID] = dealInfoRef.onSnapshot( async (snapshot) => {
            if (!snapshot.exists) {
                return;
            }
            let dealInfo = snapshot.data();
            if (dealInfo['archived'] === true) {
                callbackFnArchived(dealID);
                this.removeDealListener(dealID);
            }
            else {
                let deal = await this.getDeal(snapshot.id);
                callbackFnEdited(deal);
            }
        }, (e) => {
            console.error("Deal listener onSnapshot failed")
            console.error(e);
        });
    }

    removeDealListener(dealID) {
        let unsubscriber = unsubscribers[dealID];
        if (unsubscriber) {
            unsubscriber();
            delete unsubscribers[dealID];
        }
    }

    removeAllDealListeners() {
        let unsubs = Object.values(unsubscribers);
        for (let i = 0; i < unsubs.length; i++) {
            let unsubscriber = unsubs[i];
            if (unsubscriber) {
                unsubscriber();
            }
        }
    }

     /***** CLIENT RELATIONSHIPS *****/
     async createClientRelationship(buyerUID, sellerUID) {
         // Make sure businesses exist
         if (!buyerUID || !sellerUID) {
             throw new Error("A business must be specified");
         }
         const [ buyer, seller ] = await Promise.all([businessDB.doc(buyerUID).get(), businessDB.doc(sellerUID).get()]);
         if (!buyer.exists || !seller.exists) {
             throw new Error("The specified business does not exit");
         }
         if (buyer.data().type !== "buyer" || seller.data().type !== "seller") {
             throw new Error("Only buyer-seller relationships can be created");
         }
        await db.collection('client_relationships').add({buyerUID: buyerUID, sellerUID: sellerUID, status: 'active', sender: 'seller'});
    }

    async requestClientRelationship(buyerUID, sellerUID, sender) {
        if (sender !== 'seller' && sender !== 'buyer') {
            throw new Error("Sender must be one of seller or buyer")
        }
        // Check to make sure nothing exists already
        let querySnapshot = await db.collection('client_relationships').where('buyerUID', '==', buyerUID).where('sellerUID', '==', sellerUID).get();
        if (!querySnapshot.empty) {
            throw new Error('A client relationship has already been requested');
        }
        // Make sure businesses exist
        if (!buyerUID || !sellerUID) {
            throw new Error("A business must be specified");
        }
        const [ buyer, seller ] = await Promise.all([businessDB.doc(buyerUID).get(), businessDB.doc(sellerUID).get()]);
        if (!buyer.exists || !seller.exists) {
            throw new Error("The specified business does not exit");
        }
        if (buyer.data().type !== "buyer" || seller.data().type !== "seller") {
            throw new Error("Only buyer-seller relationships can be created");
        }

        await db.collection('client_relationships').add({buyerUID: buyerUID, sellerUID: sellerUID, status: 'requested', sender});
    }

    async getClientRelationship(buyerUID, sellerUID) {
        let querySnapshot = await db.collection('client_relationships').where('buyerUID', '==', buyerUID).where('sellerUID', '==', sellerUID).get();
        if (querySnapshot.empty) {
            return null;
        }
        let data = querySnapshot.docs[0].data();
        return new ClientRelationship(data.buyerUID, data.sellerUID, data.status, data.sender);
    }

    async acceptClientRelationship(buyerUID, sellerUID) {
        let querySnapshot = await db.collection('client_relationships').where('buyerUID', '==', buyerUID).where('sellerUID', '==', sellerUID).get();
        if (querySnapshot.empty) {
            return false;
        }
        let snapshots = querySnapshot.docs;
        // Really there should only be one such client relationship
        for (let i = 0; i < snapshots.length; i++) {
            await snapshots[i].ref.update({status: 'active'});
        }
    }

    async removeClientRelationship(buyerUID, sellerUID) {
        let querySnapshot = await db.collection('client_relationships').where('buyerUID', '==', buyerUID).where('sellerUID', '==', sellerUID).get();
        if (querySnapshot.empty) {
            return false;
        }
        let snapshots = querySnapshot.docs;
        // Really there should only be one such client relationship
        for (let i = 0; i < snapshots.length; i++) {
            await snapshots[i].ref.delete();
        }
    }

    /***** UIDS *****/
    /**
     * Returns an array of all the uid's for sellers eligible to sell
     * to the specified buyer as specified by the clientList database
     * @param {string} uid UID of the buyer
     * @return an array of all the sellerUIDs, which may be empty if
     *  there are no sellers that have listed the buyer as a client
     */
    async getSellerUIDsForBuyer(uid) {
        return this.getRelatedUIDs(uid,'buyerUID','sellerUID');
    }

    async getBuyerUIDsForSeller(uid) {
        return this.getRelatedUIDs(uid,'sellerUID','buyerUID');
    }

    async getRelatedUIDs(uid, matchUID, pairUID) {
        let query = db.collection('client_relationships').where(matchUID, '==', uid).where('status', '==', 'active');
        let dataSnapshot = await query.get();
        if (dataSnapshot.empty) {
            return [];
        }
        let arrUIDs = dataSnapshot.docs.map( (doc) => {
            return doc.data()[pairUID];
        });
        return arrUIDs;
    }

    async getPotentialClientUIDs(uid, matchUID, pairUID, sender) {
        let query = db.collection('client_relationships').where(matchUID, '==', uid).where('status', '==', 'requested').where('sender', '==', sender);
        let dataSnapshot = await query.get();
        if (dataSnapshot.empty) {
            return [];
        }
        let arrUIDs = dataSnapshot.docs.map( (doc) => {
            return doc.data()[pairUID];
        });
        return arrUIDs;
    }

    async getUIDsOfType(type) {
        let query = db.collection('businesses').where("status", "==", "active").where('type', '==', type);
        let dataSnapshot = await query.get();
        if (dataSnapshot.empty) {
            return [];
        }
        let arrUIDs = dataSnapshot.docs.map( (doc) => {
            return doc.id;
        });
        return arrUIDs;
    }

    async setPushToken(uid, token) {
        let payload = {pushToken: token}
        try {
            await db.collection('sessions').doc(uid).update(payload);
        }
        catch (e) {
            await db.collection('sessions').doc(uid).set(payload);
        }
    }

    /*
    async getSession(uid) {
        let data = await db.collection('sessions').doc(uid).get();
        if ()
        let session = new UserSession(data.notifications, data.pushToken);
        return session;
    }

    async getPushToken(uid) {
        let session = await this.getSession(uid);
        return session['pushToken'];
    }
    */

    setNotificationListener(uid, callbackFnCreated = () => {}, callbackFnModified = () => {}, callbackFnRemoved = () => {}) {
        this.removeNotificationListener();
        notificationUnsubscriber = db.collection('sessions').doc(uid).collection('notifications').where('read', '==', false).onSnapshot( (querySnapshot) => {
            querySnapshot.docChanges().forEach( (change) => {
                let data = change.doc.data();
                let notification = new Notification(change.doc.id, data.link, data.message, data.pictureURL, data.read, data.subject, data.timestamp);
                if (change.type === "added") {
                    callbackFnCreated(notification);
                }
                else if (change.type === "modified") {
                    callbackFnModified(notification);
                }
                else if (change.type === "removed") {
                    callbackFnRemoved(notification);
                }
            })
        }, (e) => {
            console.error("Notification listener onSnapshot failed")
            console.error(e);
        });
    }

    async markNotificationAsRead(uid,notificationID) {
        await db.collection('sessions').doc(uid).collection('notifications').doc(notificationID).delete();
    }

    removeNotificationListener() {
        if (notificationUnsubscriber) {
            notificationUnsubscriber();
        }
        notificationUnsubscriber = null;
    }

    removeAllListeners() {
        this.removeAllDealListeners();
        this.removeNotificationListener();
        this.removeUserListener();
        this.removeBusinessListener();
        this.removeConversationsListener();
    }

    async reportError(uid, error, info) {
        let timestamp = +(new Date());
        try {
            await db.collection('errors').add({uid, message: error.message, stack: error.stack, info, timestamp});
        }
        catch (e) {
            console.error(e);
            Sentry.captureException(e);
            console.error('Could not report error to the database', e, info);
        }
        
    }

    /***** INVITATIONS *****/
    async resendInvitation(invitationID, message) {
        const resendInvitationFunction = functions.httpsCallable('resendInvitation');
        await resendInvitationFunction({invitationID, message});
    }

    async resendInvitations(invitationIDs, message) {
        let promises = invitationIDs.map((invitationID) => {
            return this.resendInvitation(invitationID, message);
        })
        await Promise.all(promises);
    }

    /***** CERTIFICATIONS *****/
    async createCertification(certification, file) {
        let {valid, error} = certification.validate()
        if (!valid) {
            console.error(error);
            throw new Error(error.details[0].message);
        }

        let _certification = certification.formatForDB();
        const certRef = certificationDB.doc();
        
        if (file) {
            // First validate
            if (!["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(file.type)) {
                throw new Error("File type must be of the following: .pdf, .doc, .docx");
            }

            if (file.size /1024 / 1024 > MAX_FILE_SIZE) {
                throw new Error("File size must be less than " + MAX_FILE_SIZE);
            }
            
            let metadata = {
                contentType: file.type,
            };

            const certStorageRef = certificationStorage.child(certRef.id);
            await certStorageRef.put(file, metadata);
            _certification.url = await certStorageRef.getDownloadURL();
        }

        await certRef.set(_certification);


        // Now update the corresponding business
        // let business = await BusinessService.get(_certification.uid);
        // let certificationIDs = business.certifications ? business.certifications.map((certification) => certification.id) : [];
        // certificationIDs.push(certRef.id);
        // await this.updateBusiness(_certification.uid, {certifications: certificationIDs});
    }

    async getCertifications(uid, statuses = [certification_status.ACTIVE]) {
        if (!uid) {
            throw new Error("No user");
        }
        const querySnapshots = await Promise.all(statuses.map((status) => certificationDB.where("uid", "==", uid).where("status", "==", status).get()));
        const results = querySnapshots.reduce( (result, querySnapshot) => {
            if (!querySnapshot.empty) {
                querySnapshot.docs.forEach((snapshot) => {
                    if (snapshot.exists) {
                        const data = snapshot.data();
                        const cert = new Certification(data.expirationDate, snapshot.id, data.issuedDate, data.name, data.status, data.uid, data.url);
                        result.push(cert);
                    }
                })
            }
            return result;
          }, []);
        return results;
    }

    async deleteCertification(uid, certificateID) {
        await certificationDB.doc(certificateID).delete();

        // Remove it from the corresponding business too
        // let business = await BusinessService.get(uid);
        // let certs = business.certifications;
        // // Remove the relevant cert
        // var filtered = certs.filter((value, index, arr) => {
        //     return value.id !== certificateID;
        // });
        // var ids = filtered.map((cert) => cert.id);
        // await this.updateBusiness(uid, {certifications: ids});

        // Cloud functions will remove the doc in certificationStorage
        //await certificationStorage.child(certificateID).delete();
    }

    async deleteCertifications(uid, IDs) {
        await Promise.all(IDs.map( async id => {
            return this.deleteCertification(uid, id);
        }));
    }

    async getPendingCertifications(uid) {
        return this.getCertifications(uid, [certification_status.PENDING]);
    }

    async getActiveCertifications(uid) {
        return this.getCertifications(uid, [certification_status.ACTIVE]);
    }

    async getPendingActiveCertifications(uid) {
        return this.getCertifications(uid, [certification_status.ACTIVE, certification_status.PENDING]);
    }

    /** CONVERSATIONS AND MESSAGES */
    // TODO: split up file and export * as DatabaseClient

    async createDirectConversation(uid1, uid2) {
        const conversation = new Conversation(undefined, undefined, undefined, [uid1, uid2], null);
        // Check if conversation already exists
        const conv = await this.getDirectConversation(uid1, uid2);
        if (conv) {
            return conv;
        }
        const _conversation = conversation.formatForDB();
        const ref = await conversationDB.add(_conversation);
        return this.getConversationFromID(ref.id);
    }

    async getConversationFromID(conversationID) {
        const snapshot = await conversationDB.doc(conversationID).get();
        if (snapshot.exists) {
            const data = snapshot.data();
            const ts = data.timestamp ? data.timestamp.toMillis() : null;
            // Convert uids to array
            let uids = [], unread = [];
            if (data.uids) {
                uids = Object.keys(data.uids);
            }
            if (data.unread) {
                unread = Object.keys(data.unread).reduce((arr, key) => {
                    if (data.unread[key]) {
                        arr.push(key);
                    }
                    return arr;
                }, []);
            }
            const conv = new Conversation(snapshot.id, undefined, unread, uids, ts);
            await conv.init();
            return conv;
        }
        return null;
    }

    async createConversation(uids) {
        if (uids.length === 0 ||  uids.length === 1) {
            throw new Error("Not enough users to start conversation");
        }
        else if (uids.length === 2) {
            return this.createDirectConversation(uids[0], uids[1]);
        }
        else { // Group chat
            const conversation = new Conversation(undefined, undefined, undefined, uids, null);
            const _conversation = conversation.formatForDB();
            
            //_conversation.timestamp = SERVER_TIME;
            const ref = await conversationDB.add(_conversation);
            return this.getConversationFromID(ref.id);
        }
    }

    /**
     * Sends a message
     * Also resets reminderSent and resets the unread field
     * @param {*} conversationID 
     * @param {*} uid 
     * @param {*} messageText 
     * @param {*} attachment 
     */
    async sendMessage(conversationID, uid, messageText, attachment) {
        if (!messageText || messageText === '' || messageText.length > 500) {
            throw new Error("Bad message");
        }
        const message = new Message(undefined, undefined, messageText, uid, undefined, (+ new Date()));
        const _message = message.formatForDB();
        _message.timestamp = SERVER_TIME;
        if (attachment) {
            _message.attachmentID = await this.createAttachment(attachment);
        }
        await conversationDB.doc(conversationID).collection("messages").add(_message);
        const conversationSnapshot = await conversationDB.doc(conversationID).get();
        if (conversationSnapshot.exists) {
            const uids = Object.keys(conversationSnapshot.data().uids);
            // Reset reminderSent for everyone
            const reminderSent = {};
            const unread = {};
            const promises = [];
            for (let i = 0; i < uids.length; i++) {
                const uid = uids[i];
                reminderSent[uid] = false;
                if (uid !== message.senderUID) {
                    promises.push(conversationDB.doc(conversationID).update({[`unread.${uid}`]: true}));
                }
            }
            promises.push(conversationDB.doc(conversationID).update({ timestamp: _message.timestamp, reminderSent }));
            await Promise.all(promises);
        }
    }

    async createAttachment(file) {
        // First validate and compress the picture
        if (["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(file.type)) {
            let metadata = {
                contentType: file.type,
            };
            if (file.size /1024 / 1024 > MAX_FILE_SIZE) {
                throw new Error("File size must be less than " + MAX_FILE_SIZE);
            }
            let attachmentRef = attachmentDB.doc();
            let attachmentID = attachmentRef.id;
            let attachmentStorageRef = attachmentStorage.child(attachmentID);
            await attachmentStorageRef.put(file, metadata);
            let url = await attachmentStorageRef.getDownloadURL();
            await attachmentRef.set({url: url});
            return attachmentID;
        }
        // First validate and compress the picture
        else if (PictureHelper.validateImageType(file)) {
            let metadata = {
                contentType: file.type,
            };
            let compressedPic = await PictureHelper.compressImage(file);
            if (!PictureHelper.validateImageSize(compressedPic)) {
                throw new Error('Could not compress image below ' + MAX_IMAGE_SIZE + '. Please try a different image.');
            }
            let pictureRef = attachmentDB.doc();
            let pictureID = pictureRef.id;
            let pictureStorageRef = attachmentStorage.child(pictureID);
            await pictureStorageRef.put(compressedPic, metadata);
            let url = await pictureStorageRef.getDownloadURL();
            await pictureRef.set({url: url});
            return pictureID;
        }
        else {
            throw new Error("File type must be one of the following: pdf, doc, docx, png, jpg, jpeg, gif");
        }
        
    }

    async resolveAttachment(attachmentID) {
        let snapshot = await attachmentDB.doc(attachmentID).get();
        if (!snapshot.exists) {
            return null;
        }
        return snapshot.data()['url'];
    }

    async getMessagesUntil(conversationID, snapshot) {
        const query = conversationDB.doc(conversationID).collection("messages").orderBy("timestamp", "desc").endBefore(snapshot);
        return this.getMessagesHelper(query);
    }

    async getMessages(conversationID, count = DEFAULT_MESSAGE_RETRIEVAL_COUNT, lastRetrievedSnapshot) {
        let query;
        if (lastRetrievedSnapshot) {
            query = conversationDB.doc(conversationID).collection("messages").orderBy("timestamp", "desc").startAfter(lastRetrievedSnapshot).limit(count);
        }
        else {
            query = conversationDB.doc(conversationID).collection("messages").orderBy("timestamp", "desc").limit(count);
        }
        return this.getMessagesHelper(query);
    }

    async getAllMessages(conversationID) {
        const query = conversationDB.doc(conversationID).collection("messages").orderBy("timestamp", "desc");
        return this.getMessagesHelper(query);
    }

    async getMessagesHelper(query) {
        const querySnapshot = await query.get();
        if (querySnapshot.empty) {
            return [];
        }
        const messages = [];

        // Go in reverse order
        // This means the order queried was [newest, ..., oldest]
        // So the returned order will be [oldest, ..., newest]
        for (let i = querySnapshot.docs.length - 1; i > -1; i--) {
            const snapshot = querySnapshot.docs[i];
            if (snapshot.exists) {
                const data = snapshot.data();
                // If Firebase hasn't set the timestamp on the message yet, just skip
                if (!data.timestamp) {
                    continue;
                }
                const ts = data.timestamp.toMillis();

                let url;
                if (data.attachmentID) {
                    url = await this.resolveAttachment(data.attachmentID);
                }
                
                messages.push(new Message(url, snapshot.id, data.message, data.senderUID, snapshot, ts));
            }
        }
        return messages;
    }

    async getDirectConversation(uid1, uid2) {
        const querySnapshot = await conversationDB.where(`uids.${uid1}`, "==", true).where(`uids.${uid2}`, "==", true).where("numUIDs", "==", 2).get();
        if (querySnapshot.empty) {
            return null;
        }
        for (let i = 0; i < querySnapshot.docs.length; i++) {
            const snapshot = querySnapshot.docs[i];
            if (snapshot.exists) {
                const data = snapshot.data();
                const ts = data.timestamp ? data.timestamp.toMillis() : null;
                // Convert uids to array
                let uids = [], unread = [];
                if (data.uids) {
                    uids = Object.keys(data.uids);
                }
                if (data.unread) {
                    unread = Object.keys(data.unread).reduce((arr, key) => {
                        if (data.unread[key]) {
                            arr.push(key);
                        }
                        return arr;
                    }, [])
                }
                const conv = new Conversation(snapshot.id, undefined, unread, uids, ts);
                await conv.init();
                return conv;
            }
        }
        return null;
    }

    async markAsRead(conversationID, uid) {
        await conversationDB.doc(conversationID).update({
            [`unread.${uid}`]: false
        });
        // No need to reset reminderSent since the conversation is marked as read, so an email message
        // won't be sent out
        // Only reset reminderSent upon a new message
    }

    setConversationsListener(uid, callbackFnCreated = () => {}, callbackFnUpdated = () => {}, callbackFnRemoved= () => {}) {
        this.removeConversationsListener();
        // TODO: Add .limit()
        conversationsUnsubscriber = conversationDB.where('uidsArr', "array-contains", uid).orderBy("timestamp", "desc").onSnapshot( async (querySnapshot) => {
            for (let i = 0; i < querySnapshot.docChanges().length; i++) {
                const change = querySnapshot.docChanges()[i];
                let data = change.doc.data();
                // If Firebase hasn't set the timestamp on the conversation yet, just skip and
                // wait till it does set one (meaning there are messages in the conversation)
                if (!data.timestamp) {
                    continue;
                }
                const ts = data.timestamp.toMillis();

                // Convert uids to array
                let uids = [], unread = [];
                if (data.uids) {
                    uids = Object.keys(data.uids);
                }
                if (data.unread) {
                    unread = Object.keys(data.unread).reduce((arr, key) => {
                        if (data.unread[key]) {
                            arr.push(key);
                        }
                        return arr;
                    }, [])
                }
                let conv = new Conversation(change.doc.id, undefined, unread, uids, ts);
                
                if (change.type === "added") {
                    await callbackFnCreated(conv);
                }
                else if (change.type === "modified") {
                    await callbackFnUpdated(conv);
                }
                else if (change.type === "removed") {
                    await callbackFnRemoved(conv);
                }
            }
        }, (e) => {
            console.error("Conversations listener onSnapshot failed")
            console.error(e);
        });
    }

    removeConversationsListener() {
        if (conversationsUnsubscriber) {
            conversationsUnsubscriber();
        }
        conversationsUnsubscriber = null;
    }


    /***** OneSignal Push Tokens *****/
    async setOneSignalID(uid, oneSignalID) {
        if (!oneSignalID || oneSignalID.length === 0) {
            return false;
        }
        try {
            const response = await oneSignalAPI.put(`/players/${oneSignalID}`, {external_user_id: uid});
            if (response.data.success) {
                return true;
            }
        }
        catch (e) {
            console.error(e);
            Sentry.captureException(e);
            console.error("Unable to set one signal ID for uid and ID: ", uid, oneSignalID);
        }
        return false;
    }

    async removeOneSignalID(oneSignalID) {
        if (!oneSignalID || oneSignalID.length === 0) {
            return false;
        }
        try {
            const response = await oneSignalAPI.put(`/players/${oneSignalID}`, {external_user_id: ''});
            if (response.data.success) {
                return true;
            }
        }
        catch (e) {
            console.error(e);
            Sentry.captureException(e);
            console.error("Unable to remove one signal ID for ID: ", oneSignalID);
        }
        return false;
    }

    /***** Products and Product Listings *****/

    /**
     * Creates a product with an optional picture
     * @param {Product} product 
     * @param {File} picture 
     */
    async createProduct(product, picture) {
        console.log("Picture is ", picture);
        // Create a regular JS Object from the deal
        // Might throw an error
        let _product = product.formatForDB();
        if (picture) {
            _product.picture = await this.createProductPicture(picture);
        }
        _product.timestamp = +(new Date());

        const ref = await productDB.add(_product);
        return ref.id;
    }

    /**
     * Updates a product based on the id in product.id. Optionally updates the picture too
     * If a unit is removed/added, this function will invalidate/validate any associated product
     * listings accordingly with an error message (or a null error message indicating no error)
     * If a quantity is changed, this funciton will invalidate/validate any associated product listings
     * depending on if quantity is zero (product listing invalidated) or quantity is nonzero (product listing revalidated)
     * @param {Product} product 
     * @param {File} picture 
     */
    async updateProduct(product, picture) {
        let _product = product.formatForDB();
        const snapshot = await productDB.doc(product.id).get();
        if (!snapshot.exists) {
            return this.createProduct(product, picture);
        }
        if (picture) {
            _product.picture = await this.createProductPicture(picture);
            // TODO: remove previous picture
        }
        else if (picture === null) {
            _product.picture = null;
        }
        else {
            _product.picture = snapshot.data().picture || null;
        }
        _product.timestamp = +(new Date());
        await snapshot.ref.update(_product);

        // Invalidate any productlistings as necessary, or re-validate any productlistings that were invalid
        // i.e. if there is a product listing with a unit not in newUnits
        const newUnits = product.getUnitsArr();
        
        const productListingsQuery = await productListingDB.where("productID", "==", product.id).get();
            await Promise.all(productListingsQuery.docs.map(async (snapshot) => {
                if (snapshot.exists) {
                    const pl = await this.getProductListingFromSnapshot(snapshot);
                    const unit = pl.unit;
                    const error = pl.error;
                    // CASE: removed a unit, invalidating a product listing
                    if (!newUnits.includes(unit)) {
                        await snapshot.ref.update({ error: "unit:Invalid unit - unit is not listed on the associated product" });
                    }
                    else if (pl.getBaseQuantity() !== "Unlimited" && pl.getBaseQuantity() < 1) {
                        await snapshot.ref.update({ error: "product:No quantity left" });
                    }
                    // CASE:  product listing was invalid but is now valid
                    else if (error) { // We know newUnits.includes(unit)
                        await snapshot.ref.update({ error: null });
                    }
                }
        }));
    }

    /**
     * Creates a product listing
     * May set an error field on the product listing
     * @param {ProductListing} productListing 
     */
    async createProductListing(productListing, incomplete = false) {
        // Might throw an error
        let _productListing = productListing.formatForDB();
        _productListing.timestamp = +(new Date());

        // Assume no errors to begin with;
        _productListing.error = null;
        if (incomplete) {
            _productListing.error = "product:Incomplete listing"
        }
        else {
            const unit = productListing.unit;
            const product = await this.getProduct(productListing.productID);
            if (!product) {
                throw new Error("Associated product does not exist");
            }
            if (productListing.getBaseQuantity() !== "Unlimited" && productListing.getBaseQuantity() < 1) {
                _productListing.error = "product:No quantity left";
            }
    
            if (!product.getUnitsArr().includes(_productListing.unit)) {
                throw new Error("Invalid listing unit not specified on product");
                //_productListing.error = "Invalid unit - unit is not listed on the associated product";
            }
        }

        const ref = await productListingDB.add(_productListing);
        return [ref.id, _productListing.error];
    }

    /**
     * Updates a product listing based off the productListing.id
     * Will set an error if the product listing's product has insufficient quantity or
     * if the unit is not a valid unit listing in the product
     * Otherwise, the error is set to null (no error)
     * @param {ProductListing} productListing 
     */
    async updateProductListing(productListing) {
        const _productListing = productListing.formatForDB();
        const snapshot = await productListingDB.doc(productListing.id).get();
        if (!snapshot.exists) {
            return this.createProductListing(productListing);
        }

        // Assume no errors to begin with;
        _productListing.error = null;

        const unit = productListing.unit;
        const product = await this.getProduct(productListing.productID);
        if (!product) {
            throw new Error("Associated product does not exist");
        }
        if (productListing.getBaseQuantity() !== "Unlimited" && productListing.getBaseQuantity() < 1) {
            _productListing.error = "product:No quantity left";
        }
        if (!product.getUnitsArr().includes(_productListing.unit)) {
            throw new Error("Invalid unit - unit is not listed on the associated product");
        }

        await snapshot.ref.update(_productListing);
        return _productListing.error;
    }

    /**
     * Creates an entry in the pictureDB and pictureStorage and returns the ID
     * PictureDB holds documents in the format id: {name, url}
     * @param {File} picture 
     */
    async createProductPicture(picture) {
        console.log("Picture in crate is ", picture);
        // File type check
        if (!PictureHelper.validateImageType(picture)) {
            let errmsg = 'The file extension is must be one of the following: ';
            for (let i = 0; i < IMAGE_TYPES.length - 1; i++) {
                errmsg += IMAGE_TYPES[i] + ", "
            }
            errmsg += IMAGE_TYPES[IMAGE_TYPES.length - 1] + '.'
            throw new Error(errmsg);
        }
        
        
        // Compression
        let compressedPic = await PictureHelper.compressImage(picture);
        if (!PictureHelper.validateImageSize(compressedPic)) {
            throw new Error('Could not compress image below ' + MAX_IMAGE_SIZE + '. Please try a different image.');
        }

        // Extension check
        const name = picture.name;
        const metadata = {
            contentType: picture.type,
            name
        };
        // const splits = name.split('.');
        // if (splits.length < 2) {
        //     throw new Error("No file extension present");
        // }
        // const ext = splits.pop();
        
        // Upload picture
        const pictureRef = pictureDB.doc();
        const pictureID = pictureRef.id;
        // const generatedName = pictureID + "." + ext;
        const pictureStorageRef = pictureStorage.child(pictureID);
        await pictureStorageRef.put(compressedPic, metadata);
        const url = await pictureStorageRef.getDownloadURL();
        await pictureRef.set({
            url,
            name
        });
        return pictureID;
    }

    /**
     * Returns a Picture object based off the given PictureID
     * Picture object has the following structure
     * Picture: {
     *  id
     *  url
     *  name
     * }
     * @param {string} pictureID 
     */
    async getPicture(pictureID) {
        if (pictureID === undefined || pictureID === null) return null;
        const snapshot = await pictureDB.doc(pictureID).get();
        if (!snapshot.exists) {
            return null;
        }
        const data = snapshot.data();
        const picture = new Picture(snapshot.id, data.name, data.url);
        return picture;
    }

    /**
     * Gets a product based off the given ProductID
     * @param {string} productID 
     */
    async getProduct(productID) {
        const snapshot = await productDB.doc(productID).get();
        if (!snapshot.exists) {
            return null;
        }
        return this.getProductFromSnapshot(snapshot);
    }

    async getProductFromSnapshot(snapshot) {
        const data = snapshot.data();
        const id = snapshot.id;
        return this.getProductFromObj(data, id);
        
    }

    async getProductFromObj(data, id) {
        const pictureID = data.picture;
        const picture = pictureID ? await this.getPicture(pictureID) : null;
        const seller = await BusinessService.get(data.uid);
        const packFormats = Object.keys(data.packFormats).map((unit) => new PackFormat(unit, data.packFormats[unit].conversionRatio, data.packFormats[unit].conversionUnit));
        const product = new Product(data.delivery, data.description, id, data.foodCategory, data.local, data.organic,
                                    data.name, packFormats, data.pickup, picture, seller, data.quantity, data.timestamp, data.uid);
        return product;
    }

    /**
     * Gets a product listing based off the productListingID
     * @param {string} productListingID 
     */
    async getProductListing(productListingID, expectedInventory = false) {
        const snapshot = await productListingDB.doc(productListingID).get();
        if (!snapshot.exists) {
            return null;
        }
        return this.getProductListingFromSnapshot(snapshot, expectedInventory);
    }

    async getProductListingFromSnapshot(snapshot, expectedInventory = false) {
        const data = snapshot.data();
        return this.getProductListingFromObj(data, snapshot.id, undefined, expectedInventory);
    }

    /**
     * 
     * @param {Object} data 
     * @param {string} id 
     * @param {Product=} prod If not set, will retrieve the product from the database using the product ID
     */
    async getProductListingFromObj(data, id, prod, expectedInventory = false) {
        let product;
        if (!prod) {
            product = await this.getProduct(data.productID);
        }
        else {
            product = prod;
        }
        if (!product) {
            return null;
        }

        if (expectedInventory && product.quantity[data.unit] != null) {
            let quantity = product.quantity[data.unit];
            
            // Calculate the expected inventory instead of actual inventory
            // Make sure units match
            // e.g. if someone placed an order, but then product listing units changed by farmer in storefront, then don't count that
            const new_orders = await orderDB.where('productListing.id', '==', id).where('status', '==', 'created').where('unit', '==', data.unit).get();
            if (!new_orders.empty) {
                for (let i = 0; i < new_orders.docs.length; i++) {
                    let snapshot = new_orders.docs[i];
                    if (snapshot.exists) {
                        quantity -= snapshot.data().quantityRequested;
                    }
                }
            }
            // If quantity is <= 0, don't display the item to buyers
	        // This should never be called
            if (quantity <= 0) {
                return null;
            }

            product.quantity[data.unit] = quantity;
        }
        const productListing = new ProductListing(data.endListingDate, data.error, id, data.notes, product, data.productID, data.price, data.timestamp, data.uid, data.unit, data.visibility);
        return productListing;
    }

    /**
     * Deletes the specified product and any productlistings associated with it
     * @param {*} productID 
     */
    async deleteProduct(productID) {
        await productDB.doc(productID).delete();

        // Delete all product listings associated with the product
        const productListingsQuery = await productListingDB.where("productID", "==", productID).get();
        await Promise.all(productListingsQuery.docs.map(async (snapshot) => {
            if (snapshot.exists) {
                await snapshot.ref.delete();
            }
        }));
    }

    /**
     * Deletes the specified product listing
     * @param {string} productListingID 
     */
    async deleteProductListing(productListingID) {
        await productListingDB.doc(productListingID).delete();
    }


    /**
     * Gets all the product listings for a specifid uid, regardless of visibility, error flags, etc...
     * For use on the seller side. Do NOT use this function on the buyer side
     * @param {string} uid 
     */
    async getAllProductListings(uid) {
        const defaultQuery = productListingDB.where("uid", "==", uid);
        const defaultSnapshot = await defaultQuery.get();
        
        const toReturn = await Promise.all(defaultSnapshot.docs.map((snap) => this.getProductListingFromSnapshot(snap)));
        return toReturn.filter((item) => item !== null).sort((a, b) => {
            return a.product.name.localeCompare(b.product.name);
        });
    }

    /**
     * Gets all the products listings from a seller intended for viewing by a buyer
     * Adjusts inventory to be the expected inventory, not the actual inventory
     * Do NOT use this function on the seller side - should only be used on the buyer side since this avoids any listings with errors and unintended visibilities
     * If the optional buyerUID is set, more product listings will be returned that include any listings with a visibility specifically set for that uid
     * Otherwise, only product listings with the default visibility (for affiliated buyers) will be returned
     * Any product listing with the error flag set will not be returned
     * @param {string} sellerUID 
     * @param {string=} buyerUID
     */
    async getProductListingsForBuyer(sellerUID, buyerUID) {
        // Get all products with a default visibility of true
        // Get all product listings where a visibility is specifically set for a UID
        if (buyerUID) {
            const defaultQuery = productListingDB.where("uid", "==", sellerUID).where("defaultVisibility", "==", true).where("error", "==", null);
            const specificQuery = productListingDB.where("uid", "==", sellerUID).where(`visibility.${buyerUID}`, "==", true).where("error", "==", null);
            const [defaultSnapshot, specificSnapshot] = await Promise.all([defaultQuery.get(), specificQuery.get()]);
            const defaultListingsPromise = Promise.all(defaultSnapshot.docs.map((snap) => this.getProductListingFromSnapshot(snap, true)));
            const specificListingsPromise = Promise.all(specificSnapshot.docs.map((snap) => this.getProductListingFromSnapshot(snap, true)));
            let [defaultListings, specificListings] = await Promise.all([defaultListingsPromise, specificListingsPromise]);
            
            // Remove any nulls
            defaultListings = defaultListings.filter((pl) => pl !== null);
            specificListings = specificListings.filter((pl) => pl !== null);
            // If any two product listings have the same productID and offer the same unit but different visibility, user meant to use this feature to display
            // a modified price. So allow the product listing with the high visibility
            // Allow things like having to product listings offering the same product ID but diff units
            const modifiedDefaultListings = [];
            defaultListings.forEach((listing) => {
                let flag = true;
                for (let i = 0; i < specificListings.length; i++) {
                    const specificListing = specificListings[i];
                    if (listing.productID === specificListing.productID && listing.unit === specificListing.unit) {
                        flag = false; // Don't add
                        break;
                    }
                }
                if (flag) {
                    modifiedDefaultListings.push(listing);
                }
                // Otherwise, only allow the specific listing to go through
            })
            return [...specificListings, ...modifiedDefaultListings];
        }
        else {
            const defaultQuery = productListingDB.where("uid", "==", sellerUID).where("defaultVisibility", "==", true).where("error", "==", null);
            const defaultSnapshot = await defaultQuery.get();
            const toReturn = await Promise.all(defaultSnapshot.docs.map((snap) => this.getProductListingFromSnapshot(snap, true)));
            return toReturn.filter((listing) => listing !== null);
        }
    }

    /**
     * Gets the product listings for a list of sellers intended for a buyer
     * @param {*} sellerUIDs 
     * @param {*} buyerUID
     */
    async getCollectiveProductListingsForBuyer(sellerUIDs, buyerUID) {
        const collectiveSellerDeals = await Promise.all(sellerUIDs.map( async (sellerUID) =>  this.getProductListingsForBuyer(sellerUID, buyerUID)));
        var merged = [].concat.apply([], collectiveSellerDeals);
        return merged;
    }

    /**
     * Gets all the product listings for a buyer from affiliated sellers
     * @param {*} uid 
     */
    async getAffiliatedProductListings(uid) {
        const sellerUIDs = await this.getSellerUIDsForBuyer(uid);
        return this.getCollectiveProductListingsForBuyer(sellerUIDs, uid);
    }

    /***** ORGANIZATIONS *****/

    async getOrganizationById(OrgID) {
        try {
            const response = await OrganizationService.get(OrgID);
            return response;
        }
        catch(e) {
            console.error(e);
            Sentry.captureException(e);
        }
    }

    async getOrganizationBusinesses(OrgID) {
        try {
            const response = await apiService.getOrganizationAssociatedBusinesses(OrgID);
            return response;
        }
        catch(e) {
            console.error(e);
            Sentry.captureException(e);
        }
    }

    /***** ORDERS *****/

    async createOrderBundles(orders, notes) {
        if (!orders || orders.length < 1) {
            return;
        }
        // First, separate the orders out into their respective sellers
        let bundlesMap = {} // Bundles mapped by sellerUID
        let bundlesRefMap = {} // Ref to the bundles mapped by sellerUID
        for (let i = 0; i < orders.length; i++) {
            let sellerUID = orders[i]['sellerUID'];
            if (bundlesMap[sellerUID]) {
                let bundleKey = bundlesRefMap[sellerUID].id;
                orders[i].bundleID = bundleKey;
                let orderKey = await this.createOrder(orders[i]);
                
                bundlesMap[sellerUID]['orders'].push(orderKey);
                bundlesMap[sellerUID]['totalCost'] += orders[i]['totalCost'];
            }
            else {
                bundlesRefMap[sellerUID] = db.collection('order_bundles').doc();
                let bundleKey = bundlesRefMap[sellerUID].id;
                orders[i].bundleID = bundleKey;
                let orderKey = await this.createOrder(orders[i]);
                bundlesMap[sellerUID] = new OrderBundle(undefined, orders[i]['buyerUID'], undefined,
                                        [orderKey], undefined, orders[i]['sellerUID'], orders[i]['timestamp'],
                                        +orders[i]['totalCost'], notes);
            }
        }
        let bundles = Object.values(bundlesMap);
        for (let i = 0; i < bundles.length; i++) {
            let bundle = bundles[i];
            // Validate the bundle
            // Create a regular JS Object from the bundle
            let _bundle = bundle.formatForDB();

            // Validate the bundle
            const result = joi.validate(_bundle, orderBundleSchema);
            if (result.error) {
                console.error(result.error);
                throw new Error(result.error.details[0].message);
            }

            _bundle["status"] = {};
            _bundle["orders"].forEach((order) => _bundle["status"][order] = "created")
            _bundle["statusArr"] = ["created"]
            let ref = bundlesRefMap[bundle.sellerUID];
            await ref.set(_bundle);
        }
    }

    async getOrderBundlesHelper(uid, statuses, field) {
        let orderQueries;
        if (statuses) {
            orderQueries = statuses.map((status) => {
                return db.collection('orders').where(field, '==', uid).where('status', '==', status).orderBy("timestamp", "desc");
            })
            // The queries are no longer ordered by timestamp since there are multiple statuses going into this array
        }
        else {
            orderQueries = [db.collection('orders').where(field, '==', uid).orderBy("timestamp", "desc")];
        }
        let querySnapshots = await Promise.all(orderQueries.map((query) => {
            return query.get();
        }))
        let orderSnapshots = [];
        for (let i = 0; i < querySnapshots.length; i++) {
            let querySnapshot = querySnapshots[i];
            if (!querySnapshot.empty) {
                orderSnapshots = orderSnapshots.concat(querySnapshot.docs);
            }
        }
        if (orderSnapshots.length == 0) {
            return [];
        }
        
        // First, resolve all orders in parallel
        let orders = await Promise.all(orderSnapshots.map( async (orderSnapshot) => {
            let order = this.getOrderFromSnapshot(orderSnapshot);
            return order;
        }))

        // Now, group into bundles
        let bundles = {};
        for (let i = 0; i < orders.length; i++) {
            let order = orders[i];
            let bundleID = order['bundleID'];
            // Apparently we don't have to worry about race conditions here
            if (bundles[bundleID]) {
                bundles[bundleID]['orders'].push(order);
                bundles[bundleID]['totalCost'] += order['totalCost'];
            }
            else {
                bundles[bundleID] = new OrderBundle(order['buyer'], order['buyer']['uid'], bundleID, 
                                                    [order], order['seller'], order['seller']['uid'], 
                                                    order['timestamp'], +order['totalCost'])
            }
        }
        
        let arr = Object.values(bundles);
        
        // Sort by timestamp
        arr.sort( (a, b) => {
            return b.timestamp - a.timestamp;
        })
        return arr;
    }

    async getOrderBundlesForBuyer(uid, statuses) {
        return this.getOrderBundlesHelper(uid, statuses, 'buyerUID');
    }

    async getOrderBundlesForSeller(uid, statuses) {
        return this.getOrderBundlesHelper(uid, statuses, 'sellerUID');
    }

    /**
     * Creates an order
     * Intended for creating a request (status: "requested"), so a check is in 
     * place to make sure the quantity requested is <= the quantity available
     * @param {Order} order 
     */
        ////////// TODO: Match up the units esp if null

    async createOrder(order) {
        // Create a regular JS Object from the order
        let _order = order.formatForDB();
        _order.timestamp = +(new Date());
        _order.reminderSent = false;

        // Stale data check
        const productListing = await this.getProductListing(order.productListing.id);
        if (!productListing) { // Either product listing DNE or the product DNE
            throw new Error(`The product listing for ${order.productListing.product.name} is no longer available.`);
        }
        if (productListing.error) {
            throw new Error("Product listing has the following error: " + productListing.error);
        }
        // TODO: make sure price and unit haven't changed

        // Check that the quantity requested is okay
        const product = productListing.product;
        
        // CASE 0: Invalid unit
        if (order.unit !== productListing.unit) {
            throw new Error("Order unit does not match the product listing");
        }
        // CASE 1: order unit is a base unit, is not unlimited in qty, but insufficient quantity
        else if (order.unit in product.quantity && product.quantity[order.unit] !== null && product.quantity[order.unit] < order.quantityRequested) {
            throw new Error(`The quantity requested for ${product.name} is greater than the quantity available.`);
        }
        // CASE 2: order unit is a pack format, the corresponding base unit is not unimited, and the amnt of the corresponding base unit DN have sufficient
        // quantity to cover the quantity requested in terms of the base unit
        else if (order.unit in product.packFormats 
                    && product.quantity[product.packFormats[order.unit].conversionUnit] !== null 
                    && product.quantity[product.packFormats[order.unit].conversionUnit] < order.quantityRequested * product.packFormats[order.unit].conversionRatio) {
            throw new Error(`The quantity requested for ${product.name} is greater than the quantity available.`);
        }

        // Store a snapshot of the product and the product listing in the database
        const _product = product.formatForDB();
        _product.id = product.id;
        _product.picture = product.picture ? product.picture.id : null;


        const _productListing = productListing.formatForDB();
        _productListing.id = productListing.id;
        _productListing.product = _product;

        _order.productListing = _productListing;

        let ref = await orderDB.add(_order);
        return ref.id;
    }

    async getOrder(orderID) {
        const snapshot = await orderDB.doc(orderID).get();
        return this.getOrderFromSnapshot(snapshot);
    }

    async getOrderFromSnapshot(orderSnapshot) {
        if (!orderSnapshot.exists) {
            return null;
        }
        let data = orderSnapshot.data();
        return this.getOrderFromObj(data, orderSnapshot.id);
    }

    async getOrderFromObj(data, id) {
        let buyerPromise = BusinessService.get(data.buyerUID);
        let sellerPromise = BusinessService.get(data.sellerUID);
        const plData = data.productListing;
        const pData = data.productListing.product;
        const product = await this.getProductFromObj(pData, pData.id);
        const productListing = await this.getProductListingFromObj(plData, plData.id, product);
        

        let [buyer, seller] = await Promise.all([buyerPromise, sellerPromise]);
        const order = new Order(data.bundleID, buyer, data.buyerUID, productListing, data.exchange,
                                    data.exchangeStatus, id, data.needByDate, data.paymentStatus,
                                    data.price, data.quantityRequested, seller, data.sellerUID,
                                    data.status, data.timestamp, data.unit, data.totalCost);
        // Update the quantity on the order to reflect the latest amount available
        // Get a recent snapshot of the product listing
        const recentPL = await this.getProductListing(productListing.id);
        order.recentProductListing = recentPL;
        if (recentPL) {
            order.quantityAvailable = recentPL.getBaseQuantity(order.unit);
        }
        else {
            // PL doesn't exist anymore -> qty = 0
            order.quantityAvailable = 0;
        }
        return order;
    }

    async resolveOrderFields(order) {
        // Make sure not projection query
        if (!order.productListing || !order.productListing.product) {
            return order;
        }
        const plData = order.productListing;
        const pData = order.productListing.product;
        const product = await this.getProductFromObj(pData, pData.id);
        const productListing = await this.getProductListingFromObj(plData, plData.id, product);
        order.productListing = productListing;
        // Update the quantity on the order to reflect the latest amount available
        // Get a recent snapshot of the product listing
        const recentPL = await this.getProductListing(productListing.id);
        order.recentProductListing = recentPL;
        if (recentPL) {
            order.quantityAvailable = recentPL.getBaseQuantity(order.unit);
        }
        else {
            // PL doesn't exist anymore -> qty = 0
            order.quantityAvailable = 0;
        }
        return order;
    }

    async acceptRequest(orderID, syncOrderBundle = true) {
        const order = await this.getOrder(orderID);
        if (!order) {
            throw new Error("The specified order no longer exists");
        }
        if (order.availableQuantity !== "Unlimited" && order.quantityRequested > order.availableQuantity) {
            throw new Error(`Buyer requested ${order.quantityRequested} for ${order.productListing.product.name} but only ${order.availableQuantity} available.  Please refresh your page to see updated quantities.`);
        }
        let result;
        let pid;
        try {
            result = await db.runTransaction(async (transaction) => {
                let orderSnap = await transaction.get(orderDB.doc(orderID));
                if (!orderSnap.exists) {
                    throw new Error("ERROR: Order does not exist");
                }
                let order = orderSnap.data();
                pid = order.productListing.product.id;
                // Deleted product is not OK, but deleted productListing is okay as longa s the units match up
                const productSnapshot = await transaction.get(productDB.doc(order.productListing.product.id));
                if (!productSnapshot.exists) {
                    throw new Error(`ERROR: Product ${order.productListing.product.name} has been deleted. Please remove the corresponding order.`);
                }
                const product = productSnapshot.data();

                const orderedUnit = order.unit;
                let diff;
                if (!(orderedUnit in product.packFormats || orderedUnit in product.quantity)) {
                    throw new Error(`Invalid order unit ${order.unit} does not match any product units available for ${product.name}`);
                }
                else if (orderedUnit in product.packFormats) {
                    const {conversionRatio, conversionUnit} = product.packFormats[orderedUnit];
                    if (!(conversionUnit in product.quantity)) {
                        throw new Error(`ERROR: No associated base unit ${conversionUnit} listed in ${order.productListing.product.name} for ${orderedUnit}.  Please remove the corresponding order.`);
                    }
                    // Not unlimited qty
                    if (product.quantity[conversionUnit] !== null) {
                        const baseUnitAmnt = order.quantityRequested * conversionRatio;
                        if (baseUnitAmnt > product.quantity[conversionUnit]) {
                            throw new Error(`${order.quantityRequested} ${orderedUnit}(s) (${baseUnitAmnt} ${conversionUnit}(s)) requested but only ${product.quantity[conversionUnit]} ${conversionUnit}(s) available`);
                        }
                        diff = product.quantity[conversionUnit] - baseUnitAmnt;

                        await transaction.update(productSnapshot.ref, {[`quantity.${conversionUnit}`] : diff });

                        
                        // productUpdates.quantity = {
                        //     [conversionUnit]: diff
                        // }
                    }
                }
                else if (orderedUnit in product.quantity && product.quantity[orderedUnit] !== null) {
                    if (order.quantityRequested > product.quantity[orderedUnit]) {
                        throw new Error(`${order.quantityRequested} ${orderedUnit}(s) requested but only ${product.quantity[orderedUnit]} ${orderedUnit}(s) available`);
                    }
                    diff = product.quantity[orderedUnit] - order.quantityRequested;

                    await transaction.update(productSnapshot.ref, {[`quantity.${orderedUnit}`] : diff });


                    // productUpdates.quantity = {
                    //     [orderedUnit]: diff
                    // }
                }

                // Dummy update b/c FB requires all documents read to also be written
                await transaction.update(productSnapshot.ref, {});

                // Accept the order
                await transaction.update(orderSnap.ref, {status: "accepted"});

                return diff;
            });
            // Check for No quantity left
            // Don't need transaction for this
            // Invalidate all product listings associated with a product if insufficient quantity
            if (pid) {
                const productListingsQuery = await productListingDB.where("productID", "==", pid).get();
                await Promise.all(productListingsQuery.docs.map(async (snapshot) => {
                    if (snapshot.exists) {
                        const pl = await this.getProductListingFromSnapshot(snapshot);
                        if (pl.getBaseQuantity() !== "Unlimited" && pl.getBaseQuantity() < 1) {
                            await snapshot.ref.update({ error: "product:No quantity left" });
                        }
                    }
                }));
            }
            if (syncOrderBundle) {
                await this.syncOrderBundle(order.bundleID);
            }
        }
        catch (e) {
            console.error(e);
            Sentry.captureException(e);
            throw new Error(e);
            // if (e.message === "ERROR: Order does not exist") {
            //     throw(e);
            // }
            // else if (e.message === "The quantity requested is greater than the quantity available.") {
            //     throw (e);
            // }
            // else {
            //     console.error("Transaction failed");
            //     console.error(e);
            //     throw new Error("Was unable to process order due to heavy load, please try again");
            // }
        }
        return result;
    }

    async acceptOrderBundle(orderBundleID) {
        const bundle = await OrderBundleService.get(orderBundleID);
        try {
            await Promise.all(bundle.orders.map((order) => this.acceptRequest(order.id, false)));
        } finally {
            await this.syncOrderBundle(orderBundleID);
        }
    }

    async syncOrderBundle(orderBundleID) {
        await db.runTransaction(async (transaction) => {
            const updates = {};
            let bundleSnap = await transaction.get(orderBundleDB.doc(orderBundleID));
            if (bundleSnap.exists) {
                const orderBundle = bundleSnap.data();
                if (orderBundle.orders) {
                    const orders = await Promise.all(orderBundle.orders.map((orderID) => transaction.get(orderDB.doc(orderID))));
                    updates.status = {};
                    updates.statusArr = [];
                    orders.forEach((orderSnap) => {
                        if (orderSnap.exists) {
                            const order = orderSnap.data();
                            updates.status[orderSnap.id] = order.status;
                            updates.statusArr.push(order.status);
                        }
                    });
                    updates.statusArr = updates.statusArr.filter((v,i) => updates.statusArr.indexOf(v) === i);

                    // Dummy updates so Firestore doesn't yell at us
                    await Promise.all(orders.map((orderSnap) => transaction.update(orderSnap.ref, {})))
                }
            }
            // Do this even if a dummy update of {}
            await transaction.update(bundleSnap.ref, updates);
        })
    }

    async updateOrder(orderID, updates, syncOrderBundle = true) {
        await db.collection('orders').doc(orderID).update(updates);
        if (syncOrderBundle) {
            const snap = await orderDB.doc(orderID).get();
            if (snap.exists) {
                const orderBundleID = snap.data().bundleID;
                await this.syncOrderBundle(orderBundleID);
            }
        }
    }

    async updateOrderBundleOrders(orderBundleID, updates) {
        const bundle = await OrderBundleService.get(orderBundleID);
        try {
            await Promise.all(bundle.orders.map((order) => this.updateOrder(order.id, updates, false)));
        } finally {
            await this.syncOrderBundle(orderBundleID);
        }
    }

    /***** SESSIONS *****/
    /**
     * Inserts the given productListingID into the basket
     * @param {*} uid 
     * @param {string[]} productListingID
     * @throws an erorr when the product listing does not exist
     */
    async addToBasket(uid, productListingID) {
        // Verify the productListingID is valid
        const productListing = await this.getProductListing(productListingID, true);
        if (!productListing) {
            throw new Error("Product listing does not exist");
        }

        const sessionSnapshot = await sessionDB.doc(uid).get();
        if (sessionSnapshot.exists) {
            await sessionDB.doc(uid).update({ [`basket.${productListingID}`] : true });
        }
        else {
            await sessionDB.doc(uid).set({ basket: { [`productListingID`] : true } });
        }
        return productListing;
    }

    /**
     * Gets the basket as an array of productListingIDs or returns []
     * @param {string} uid 
     */
    async getBasket(uid) {
        const ids = await this.getBasketListingIDs(uid);
        const basket = await Promise.all(ids.map( async (id) => {
            const listing = await this.getProductListing(id, true);
            if (!listing) {
                try {
                    await this.removeFromBasket(uid, id);
                } catch (e) {
                    console.error(e);
                    Sentry.captureException(e);
                    // Do nothing
                    console.error(e);
                }
                return null;
            }
            else {
                return listing;
            }
        }));

        // Return only truthy items
        return basket.filter((item) => item != null);
    }

    async getBasketListingIDs(uid) {
        const snapshot = await sessionDB.doc(uid).get();
        if (!snapshot.exists || !snapshot.get('basket')) {
            return [];
        }
        return Object.keys(snapshot.get('basket'));
    }

    /**
     * Removes the given productListingID from the basket
     * @param {string} uid 
     * @param {string} productListingID 
     */
    async removeFromBasket(uid, productListingID) {
        await sessionDB.doc(uid).update({ [`basket.${productListingID}`]: firebase.firestore.FieldValue.delete() });
        // const basket = await this.getBasketListingIDs(uid);
        // const index = basket.indexOf(productListingID);
        // if (index !== -1) {
        //     basket.splice(index, 1);
        //     await sessionDB.doc(uid).update({ basket });
        // }
    }

    async clearBasket(uid) {
        await sessionDB.doc(uid).update({ basket: {} });
    }

    async editOrder(orderID, needByDate, quantityRequested, exchange, price, totalCost) {
        const order = await this.getOrder(orderID);
        const updates = {};
        if (!order) {
            throw new Error("Order does not exist");
        }
        if (needByDate) {
            order.needByDate = +moment(needByDate).startOf("day");
            updates.needByDate = order.needByDate;
        }
        else {
            updates.needByDate = null;
        }
        if (quantityRequested) {
            order.quantityRequested = quantityRequested;
            updates.quantityRequested = order.quantityRequested;
        }
        if (exchange) {
            order.exchange = exchange;
            updates.exchange = order.exchange;
        }
        if (price) {
            if (price < 0) { // Allow 0 price
                throw new Error("Price must be non-negative");
            }
            order.price = price;
            updates.price = price;
        }
        if (totalCost) {
            if (totalCost < 0) {
                throw new Error("Total cost must be non-negative");
            }
            order.totalCost = totalCost;
            updates.totalCost = totalCost;
        }
        // Validate, may throw an error
        // Allow edit qty to surpass limits if order is accepted
        order.validate(order.isAccepted());
        updates.lastUpdatedBy = profileStore.business.id;
        // This does not update the quantity per inventory
        // Just a regular update method
        // TODO: Add inventory adjustment looking at before/after quantity
        await this.updateOrder(orderID, updates);
    }
}

export default new DatabaseClient()
